import { PACKAGE_CONTENT_KEYS } from '@app/constant/package-content-keys';
import type { Package } from '@app/interface/store/package.interface';
import type { AddOn } from '@app/interface/store/add-on.interface';
import type {
  ParsedItem,
  ParseResult,
  MatchResult,
  Sheet,
  CombinedSheet,
  ParsedContentItem,
  CombinedResultItem,
  LayoutThemeLookKey,
  ContentReader,
  TagContext
} from '@app/interface/package/package-details.interface';

/**
 * Recursive method.
 * Will attempt to parse item names, will return item objects and/or if parse was successful
 *   must be in the format '(n) wxh description'
 *   examples:
 *     correct: '(1) 8x10', '(4) 3x7 Wallets', '(2) 5x7, (4) 3x7 Wallets'
 *     incorrect: '(4) Variety 3x7', '2-5x7 Individuals'
 * @param itemName - item name, ex. '(1) 8x10'
 * @param items - optionally pass previously parsed items
 * @returns {{success: boolean, items: *}}
 */
// \u00C0-\u00FF (Latin-1 Supplement) to cover French and Spanish
const bracesRegex = /\((\d+)\)\s*(\d+)[xX](\d+)([\s\w\u00C0-\u00FF]*)/; // (4) 3x7 Wallets
const hyphenRegex = /(\d+)\s*[-]\s*(\d+)[xX](\d+)([\s\w\u00C0-\u00FF]*)/; // 4 - 3x7 Wallets
const bracesWithoutWidthHeightRegex = /\((\d+)\)\s*([\s\w\u00C0-\u00FF]*)/; //  (1) 8x10
const hyphenWithoutWidthHeightRegex = /(\d+)\s*[-]\s*([\s\w\u00C0-\u00FF]*)/; //  1 - 8x10
const doubleHyphenWithoutWidthHeightRegex = /(\d+)\s*[-]\s*([\s\w\u00C0-\u00FF]*)\s*[-]\s*([\s\w\u00C0-\u00FF]*)/; // 1 - Digital Image - Background of your choice
const hyphenInludedRegex = /[\s\w\u00C0-\u00FF]+\s*[-]\s*[\s\w\u00C0-\u00FF]+/; // Digital Image - Background of your choice
const notStartWithCountBracesRegex = /^(?!\((\d+)\))/; // not start with count like '(5)'
const notStartWithCountHyphenRegex = /^(?!(\d+)\s*[-])/; // not start with count like '5 -'
const anyStringPattern = /[\s\w\u00C0-\u00FF]+/;

const findMatch = (itemName: string): MatchResult | null => {
  const notStartWithCount = notStartWithCountBracesRegex.test(itemName) || notStartWithCountHyphenRegex.test(itemName);

  const candidates: RegExp[] = [
    bracesRegex,
    hyphenRegex,
    bracesWithoutWidthHeightRegex,
    doubleHyphenWithoutWidthHeightRegex,
    hyphenWithoutWidthHeightRegex
  ];

  for (const regex of candidates) {
    if (regex.test(itemName)) {
      const result = regex.exec(itemName);
      if (result) return { result, regex };
    }
  }

  if (notStartWithCount) {
    if (hyphenInludedRegex.test(itemName)) {
      const result = hyphenInludedRegex.exec(itemName);
      if (result) return { result, regex: hyphenInludedRegex };
    } else if (anyStringPattern.test(itemName.trim())) {
      const result = anyStringPattern.exec(itemName);
      if (result) return { result, regex: anyStringPattern };
    }
  }

  return null;
};

const buildItemObj = (result: RegExpExecArray): ParsedItem => {
  if (result.length >= 5) {
    return {
      count: parseInt(result[1]),
      width: parseInt(result[2]),
      height: parseInt(result[3]),
      description: result[4]
    };
  }
  if (result.length === 3) {
    return {
      count: parseInt(result[1]),
      description: result[2]
    };
  }
  if (result.length > 3) {
    return {
      count: parseInt(result[1]),
      description: result[2].trim() + (result[3] ? ` - ${result[3].trim()}` : '')
    };
  }
  return {
    count: 1,
    description: result[0] ? result[0].trim() : ''
  };
};

export const parseItemName = (itemName: string, items?: ParsedItem[]): ParseResult => {
  try {
    const match = findMatch(itemName);

    if (!match) {
      if (itemName.replace(',', '').trim().length !== 0) {
        return { success: false };
      }
      return { success: true, items };
    }

    const itemObj = buildItemObj(match.result);
    const allItems = !items ? [itemObj] : items.concat(itemObj);
    return parseItemName(itemName.replace(match.regex, ''), allItems);
  } catch {
    return { success: false };
  }
};
/**
 * Recursive method.
 * Will combine sheets based on height and width of the sheet. Will use first available description
 * @param sheets { [{count:number, height:number, width:number, description?:string} ] }
 * @param combinedSheets - optionally pass previously combined sheets
 * @returns { [{nameDesc:string, height:number, width:number}] }
 */
const combineSheets = (sheets: Sheet[], combinedSheets?: CombinedSheet[]): CombinedSheet[] => {
  if (sheets.length < 1) {
    return combinedSheets ?? [];
  }

  const sheet = sheets[0];

  const matchingSheets = sheets.filter(
    (itSheet: Sheet) => sheet.width === itSheet.width && sheet.height === itSheet.height
  );

  const otherSheets = sheets.filter(
    (itSheet: Sheet) => sheet.width !== itSheet.width || sheet.height !== itSheet.height
  );

  // first try and use the first sheet's description, then try to find another non-empty description for the sheet size
  let sheetDescription = sheet.description;
  if (!sheetDescription) {
    const otherSheet = matchingSheets.find((itSheet: Sheet) => itSheet.description !== '');
    sheetDescription = otherSheet ? otherSheet.description : '';
  }

  const totalCount = matchingSheets.reduce((acc: number, item: Sheet) => acc + (item.count ?? 0), 0);

  const combinedSheet: CombinedSheet = {
    nameDesc: `${totalCount} - ${sheet.width}x${sheet.height}${sheetDescription}`,
    width: sheet.width,
    height: sheet.height
  };

  return combineSheets(otherSheets, !combinedSheets ? [combinedSheet] : combinedSheets.concat(combinedSheet));
};

const combineSheetsWithoutDimensions = (sheets: Sheet[], combinedSheets?: CombinedSheet[]): CombinedSheet[] => {
  if (sheets.length < 1) {
    return combinedSheets ?? [];
  }

  const sheet = sheets[0];

  const matchingSheets = sheets.filter(
    (itSheet: Sheet) =>
      !sheet.width && !sheet.height && !itSheet.width && !itSheet.height && sheet.description === itSheet.description
  );

  const otherSheets = sheets.filter(
    (itSheet: Sheet) =>
      !sheet.width && !sheet.height && !itSheet.width && !itSheet.height && sheet.description !== itSheet.description
  );

  // first try and use the first sheet's description, then try to find another non-empty description for the sheet size
  let sheetDescription = sheet.description;
  if (!sheetDescription) {
    const otherSheet = matchingSheets.find((itSheet: Sheet) => itSheet.description !== '');
    sheetDescription = otherSheet ? otherSheet.description : '';
  }

  const totalCount = matchingSheets.reduce((acc: number, item: Sheet) => acc + (item.count ?? 1), 0);

  const combinedSheet: CombinedSheet = {
    nameDesc: `${totalCount} - ${sheetDescription}`
  };

  return combineSheetsWithoutDimensions(
    otherSheets,
    !combinedSheets ? [combinedSheet] : combinedSheets.concat(combinedSheet)
  );
};

/**
 * Parses and combines items from a catalog package items array.
 * See trap 5.1: returns mixed shapes ({nameDesc, width, height} and {item, nameDesc, nameParse}).
 * See trap 5.3: sort order is load-bearing — dimensioned sheets sorted by area descending.
 */
export const parseAndCombine = (items: AddOn[]): CombinedResultItem[] => {
  const contents: ParsedContentItem[] = items.map((item: AddOn) => ({
    item,
    nameDesc: item.nameDesc,
    nameParse: parseItemName(item.nameDesc)
  }));

  // filter out sheets that failed parse to show at the end of the list
  const failedSheetItems = contents.filter((item) => !item.nameParse.success);

  const parsedSheets = contents.reduce((acc: Sheet[], item: ParsedContentItem) => {
    if (item.nameParse.success) {
      item.nameParse.items?.forEach((parsedItem: ParsedItem) => {
        if (!!parsedItem.width && !!parsedItem.height) {
          acc = acc.concat(parsedItem);
        }
      });
    }
    return acc;
  }, []);

  const rawSheetsWithoutDimensions: Sheet[] = contents.reduce((acc: Sheet[], item: ParsedContentItem) => {
    if (item.nameParse.success) {
      item.nameParse.items?.forEach((parsedItem: ParsedItem) => {
        if (!parsedItem.width || !parsedItem.height) {
          acc = acc.concat(parsedItem);
        }
      });
    }
    return acc;
  }, []);

  // combine and sort sheets
  const combinedSheets = [...combineSheets(parsedSheets)].sort(
    (sheetA: CombinedSheet, sheetB: CombinedSheet) =>
      // sort from the largest sheet size to smallest
      (sheetB.width ?? 0) * (sheetB.height ?? 0) - (sheetA.width ?? 0) * (sheetA.height ?? 0)
  );

  const sheetsWithoutDimensions: CombinedSheet[] = [...combineSheetsWithoutDimensions(rawSheetsWithoutDimensions)];

  return ([] as CombinedResultItem[]).concat(combinedSheets).concat(sheetsWithoutDimensions).concat(failedSheetItems);
};

export const getPackageSheets = (pkg: Package): CombinedResultItem[] => {
  const sheetItems = pkg.items.filter(
    (item: AddOn) => item.lookSelectionAllowed && item.productCategory === 'PortSheets'
  );
  return parseAndCombine(sheetItems);
};

export const getPackageProducts = (pkg: Package): CombinedResultItem[] => {
  const products = pkg.items.filter(
    (item: AddOn) => !item.lookSelectionAllowed || item.productCategory !== 'PortSheets'
  );

  if (products.length > 0) {
    return parseAndCombine(products).sort((a: CombinedResultItem, b: CombinedResultItem) =>
      ((a as CombinedSheet).nameDesc || '').localeCompare((b as CombinedSheet).nameDesc || '')
    );
  }
  return products;
};

// for standardized price packages
export const getAllPackageItems = (pkg: Package): CombinedResultItem[] => {
  return parseAndCombine(pkg.items);
};

export const getPrintsDeliveredCount = (items: AddOn[]): number => {
  let printsDeliveryCount = 0;
  items.forEach((item) => {
    if (!item.digitalDelivery) {
      const parsedNameDesc = parseItemName(item.nameDesc);
      if (parsedNameDesc.success) {
        parsedNameDesc.items?.forEach((parsedItem: ParsedItem) => {
          printsDeliveryCount += parsedItem.count;
        });
      }
    }
  });
  return printsDeliveryCount;
};

export const getDigitalDeliveredCount = (items: AddOn[]): number => {
  let digitalDeliveredCount = 0;
  const layoutThemeIdLookNumberList: LayoutThemeLookKey[] = [];
  items.forEach((item) => {
    if (item.canBeDigitalEligible) {
      if (item.lookSelectionAllowed) {
        digitalDeliveredCount++;
      } else {
        const themeIdLookNumberExists = !!layoutThemeIdLookNumberList.find(
          (obj: LayoutThemeLookKey) =>
            obj.defaultLayoutThemeId === item.defaultLayoutThemeId && obj.defaultLookNumber === item.defaultLookNumber
        );
        if (!themeIdLookNumberExists) {
          digitalDeliveredCount++;
          layoutThemeIdLookNumberList.push({
            defaultLayoutThemeId: item.defaultLayoutThemeId,
            defaultLookNumber: item.defaultLookNumber
          });
        }
      }
    }
  });
  return digitalDeliveredCount;
};

/**
 * Reads a content string as a whole, non-negative integer.
 *
 * Rejects floats ('3.7'), malformed values ('12abc', 'abc12'), negatives and
 * blanks, so a bad content entry falls back to the catalog value instead of
 * rendering a wrong count. '0' is valid and meaningful — it hides the count.
 *
 * Also rejects a bare content key, which is what ContentStore.getContent
 * returns for content-translation admins (see ContentStore.js:243-245).
 */
export const toDeliveryCount = (raw: string | undefined | null): number | undefined => {
  if (raw == null || raw === '') return undefined;
  const trimmed = raw.trim();
  if (!/^-?\d+$/.test(trimmed)) return undefined;
  const parsed = parseInt(trimmed, 10);
  if (!Number.isFinite(parsed) || parsed < 0) return undefined;
  return parsed;
};

export const resolveTag = (read: ContentReader, { zone, label, isPbtfPilot }: TagContext) => {
  const defaultLabel = (label && read(zone, label)) ?? read(zone, PACKAGE_CONTENT_KEYS.TAG);
  const defaultStyle = read(zone, PACKAGE_CONTENT_KEYS.TAG_STYLE);

  if (!isPbtfPilot) return { label: defaultLabel, tagStyle: defaultStyle };

  return {
    label: read(zone, PACKAGE_CONTENT_KEYS.TAG_PBTF) ?? defaultLabel,
    tagStyle: read(zone, PACKAGE_CONTENT_KEYS.TAG_STYLE_PBTF) ?? defaultStyle
  };
};
